import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Table, Button, Modal, Form, Alert, Badge, Pagination } from 'react-bootstrap';
import { authService } from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import LoadingIndicator from '../components/LoadingIndicator';
import { useLocalization } from '../context/LocalizationContext';
import { UserDto } from '../types';
import './UserManagement.css';

export const UserManagement: React.FC = () => {
  const { t, formatDate } = useLocalization();
  const tRef = useRef(t);
  const [users, setUsers] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [userFormValidated, setUserFormValidated] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    role: 'User',
  });
  const { user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await authService.getAllUsers();
      setUsers(data);
    } catch (err) {
      setError(tRef.current('failedToLoadUsers'));
      console.error(err);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  const handleShowModal = (editUser?: UserDto) => {
    setUserFormValidated(false);

    if (editUser) {
      setEditingId(editUser.id);
      setFormData({
        username: editUser.username,
        email: editUser.email,
        firstName: editUser.firstName,
        lastName: editUser.lastName,
        password: '',
        role: editUser.role,
      });
    } else {
      setEditingId(null);
      setFormData({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        role: 'User',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
      if (isSaving) return;
    setShowModal(false);
    setEditingId(null);
    setUserFormValidated(false);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isValidEmail = (value: string): boolean => /^\S+@\S+\.\S+$/.test(value.trim());

  const isPasswordValid = (): boolean => {
    const trimmedPassword = formData.password.trim();

    if (editingId) {
      return trimmedPassword.length === 0 || trimmedPassword.length >= 6;
    }

    return trimmedPassword.length >= 6;
  };

  const isUserFormValid = (): boolean => {
    const requiresUsername = editingId === null;

    return (
      (!requiresUsername || formData.username.trim().length > 0)
      && formData.email.trim().length > 0
      && isValidEmail(formData.email)
      && formData.firstName.trim().length > 0
      && formData.lastName.trim().length > 0
      && isPasswordValid()
    );
  };

  const handleSaveUser = async () => {
    setUserFormValidated(true);

    if (!isUserFormValid()) {
      if (!isValidEmail(formData.email)) {
        alert(t('validEmailRequired'));
      } else if (!isPasswordValid()) {
        alert(t('passwordMinChars'));
      } else {
        alert(t('pleaseCompleteRequiredFields'));
      }
      return;
    }

    const payload = {
      ...formData,
      username: formData.username.trim(),
      email: formData.email.trim(),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      password: formData.password.trim(),
    };

    try {
        setIsSaving(true);
      if (editingId) {
        await authService.updateUser(editingId, payload);
        alert(t('userUpdatedSuccessfully'));
      } else {
        await authService.createUser(payload);
        alert(t('userCreatedSuccessfully'));
      }
      handleCloseModal();
      loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.message || t('failedToSaveUser'));
      console.error(err);
      } finally {
        setIsSaving(false);
    }
  };

  const handleDeleteUser = async (id: number) => {
    if (user?.id === id) {
      alert(t('cannotDeleteOwnAccount'));
      return;
    }

    if (window.confirm(t('confirmDeleteUser'))) {
      try {
        await authService.deleteUser(id);
        alert(t('userDeletedSuccessfully'));
        loadUsers();
      } catch (err) {
        alert(t('failedToDeleteUser'));
        console.error(err);
      }
    }
  };

  const totalPages = Math.max(1, Math.ceil(users.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStartIndex = (safeCurrentPage - 1) * pageSize;
  const pagedUsers = users.slice(pageStartIndex, pageStartIndex + pageSize);
  const pageStart = users.length === 0 ? 0 : pageStartIndex + 1;
  const pageEnd = users.length === 0 ? 0 : Math.min(users.length, pageStartIndex + pageSize);

  if (loading && !hasLoadedOnce) {
    return (
      <Container className="py-5 page-loading-shell">
        <div className="page-loading-header">
          <div className="page-loading-title page-skeleton-shimmer" aria-hidden="true" />
          <div className="page-loading-subtitle">
            <LoadingIndicator label={t('loading')} className="system-loading-status-card-inline" showLines={false} />
          </div>
        </div>
        <div className="row g-4">
          <div className="col-lg-4 col-md-6">
            <div className="page-loading-block page-skeleton-shimmer" aria-hidden="true" />
          </div>
          <div className="col-lg-8 col-md-6">
            <div className="page-loading-block page-loading-block-tall page-skeleton-shimmer" aria-hidden="true" />
          </div>
          <div className="col-12">
            <div className="page-loading-block page-skeleton-shimmer" aria-hidden="true" />
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-5 user-management-page" aria-busy={loading || isSaving}>
      <div className="user-management-hero page-header-panel">
        <div className="user-management-header">
          <h1 className="mb-0 page-header-title">
            <span className="page-header-title-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                <circle cx="9" cy="8" r="3" fill="none" stroke="currentColor" strokeWidth="1.9" />
                <path d="M3.7 18a5.6 5.6 0 0 1 10.6 0" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                <circle cx="17.4" cy="9.4" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.9" />
                <path d="M15 18a4.6 4.6 0 0 1 5.1-3.7" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
              </svg>
            </span>
            {t('userManagementTitle')}
          </h1>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="user-management-tab-panel">
        <div className="d-flex justify-content-between align-items-center mb-4 user-management-section-header">
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0 user-management-section-title">{t('usersLabel')}</h5>
            <span className="user-management-section-count">{users.length}</span>
          </div>
          <Button variant="primary" className="user-management-primary-action" onClick={() => handleShowModal()} disabled={loading}>
            {t('addNewUser')}
          </Button>
        </div>

        {users.length === 0 && !loading ? (
          <Alert variant="info" className="mb-0">{t('noUsersFound')}</Alert>
        ) : (
          <div className="user-management-grid-panel">
            <div className="table-responsive standard-grid-shell dashboard-table-wrapper">
              <Table className="dashboard-table mb-0 user-management-table">
                <thead>
                  <tr>
                    <th>{t('username')}</th>
                    <th>{t('email')}</th>
                    <th>{t('name')}</th>
                    <th>{t('role')}</th>
                    <th>{t('created')}</th>
                    <th>{t('lastLogin')}</th>
                    <th>{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        <div className="dashboard-grid-loading-message">
                          <LoadingIndicator label={t('loading')} subtle />
                        </div>
                      </td>
                    </tr>
                  ) : pagedUsers.map((u) => (
                    <tr key={u.id}>
                      <td>{u.username}</td>
                      <td>{u.email}</td>
                      <td>{u.firstName} {u.lastName}</td>
                      <td>
                        <Badge className={u.role === 'Admin' ? 'user-role-badge is-admin' : 'user-role-badge is-user'}>
                          {u.role === 'Admin' ? t('adminRole') : t('userRole')}
                        </Badge>
                      </td>
                      <td>{formatDate(u.createdDate)}</td>
                      <td>
                        {u.lastLoginDate
                          ? formatDate(u.lastLoginDate)
                          : t('never')}
                      </td>
                      <td className="standard-grid-action-cell">
                        <div className="standard-grid-action-wrap">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="user-management-action-btn"
                            onClick={() => handleShowModal(u)}
                            disabled={loading}
                          >
                            {t('edit')}
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="user-management-action-btn"
                            onClick={() => handleDeleteUser(u.id)}
                            disabled={user?.id === u.id || loading}
                          >
                            {t('delete')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="dashboard-table-footer d-flex justify-content-between align-items-center user-management-table-meta">
              <div className="dashboard-table-meta text-muted">
                {t('showing')} {pageStart}-{pageEnd} {t('of')} {users.length}
              </div>
              <div className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0 dashboard-rows-label">{t('rows')}</Form.Label>
                <Form.Select
                  className="dashboard-rows-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                  disabled={loading}
                  style={{ width: '90px' }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </Form.Select>
                <Pagination className="mb-0 dashboard-pagination">
                  <Pagination.First onClick={() => setCurrentPage(1)} disabled={safeCurrentPage === 1 || loading} />
                  <Pagination.Prev onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={safeCurrentPage === 1 || loading} />
                  <Pagination.Item active>{safeCurrentPage}</Pagination.Item>
                  {totalPages > 1 && <Pagination.Item disabled>/ {totalPages}</Pagination.Item>}
                  <Pagination.Next
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={safeCurrentPage === totalPages || loading}
                  />
                  <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={safeCurrentPage === totalPages || loading} />
                </Pagination>
              </div>
            </div>
          </div>
        )}

      </div>

      <Modal show={showModal} onHide={handleCloseModal} className="user-management-modal" centered>
        <Modal.Header closeButton={!isSaving}>
          <Modal.Title>
            {editingId ? t('editUser') : t('createNewUser')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body aria-busy={isSaving}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('username')}</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                onChange={handleFormChange}
                disabled={editingId !== null}
                onBlur={() => setUserFormValidated(true)}
                isInvalid={userFormValidated && editingId === null && formData.username.trim().length === 0}
              />
              <Form.Control.Feedback type="invalid">{t('fieldRequired')}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('email')}</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleFormChange}
                onBlur={() => setUserFormValidated(true)}
                isInvalid={userFormValidated && !isValidEmail(formData.email)}
              />
              <Form.Control.Feedback type="invalid">{t('validEmailRequired')}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('firstName')}</Form.Label>
              <Form.Control
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleFormChange}
                onBlur={() => setUserFormValidated(true)}
                isInvalid={userFormValidated && formData.firstName.trim().length === 0}
              />
              <Form.Control.Feedback type="invalid">{t('fieldRequired')}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('lastName')}</Form.Label>
              <Form.Control
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleFormChange}
                onBlur={() => setUserFormValidated(true)}
                isInvalid={userFormValidated && formData.lastName.trim().length === 0}
              />
              <Form.Control.Feedback type="invalid">{t('fieldRequired')}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('password')} {editingId && `(${t('leaveBlankKeepCurrent')})`}</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleFormChange}
                onBlur={() => setUserFormValidated(true)}
                isInvalid={userFormValidated && !isPasswordValid()}
              />
              <Form.Control.Feedback type="invalid">{t('passwordMinChars')}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('role')}</Form.Label>
              <Form.Select
                name="role"
                value={formData.role}
                onChange={handleFormChange}
              >
                <option value="User">{t('userRole')}</option>
                <option value="Admin">{t('adminRole')}</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal} disabled={isSaving}>
            {t('cancel')}
          </Button>
          <Button variant="primary" className="user-management-primary-action d-inline-flex align-items-center gap-2" onClick={handleSaveUser} disabled={isSaving || !isUserFormValid()}>
            {isSaving && <LoadingIndicator label={t('savingAction')} inline compact showLines={false} />}
            {isSaving ? t('savingAction') : editingId ? t('update') : t('add')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserManagement;
